/**
 * Wall Connection System - manages wall segment connections and variant updates
 * Walls automatically connect to adjacent walls and update their visual variant
 */

import type { Entity } from '@/types';
import { WallDirection, type WallVariant, type WallComponent } from '@/game/components/Wall';

// Variant and rotation lookup based on connection bitmask
interface VariantInfo {
  variant: WallVariant;
  rotation: number;
}

// Map connection bitmask to variant and rotation
const CONNECTION_TO_VARIANT: Record<number, VariantInfo> = {
  // No connections
  0: { variant: 'single', rotation: 0 },

  // One connection (end)
  [WallDirection.NORTH]: { variant: 'end', rotation: 0 },
  [WallDirection.EAST]: { variant: 'end', rotation: -Math.PI / 2 },
  [WallDirection.SOUTH]: { variant: 'end', rotation: Math.PI },
  [WallDirection.WEST]: { variant: 'end', rotation: Math.PI / 2 },

  // Two connections - straight (opposite directions)
  [WallDirection.NORTH | WallDirection.SOUTH]: { variant: 'straight', rotation: 0 },
  [WallDirection.EAST | WallDirection.WEST]: { variant: 'straight', rotation: Math.PI / 2 },

  // Two connections - corner (adjacent directions)
  [WallDirection.NORTH | WallDirection.EAST]: { variant: 'corner', rotation: 0 },
  [WallDirection.EAST | WallDirection.SOUTH]: { variant: 'corner', rotation: -Math.PI / 2 },
  [WallDirection.SOUTH | WallDirection.WEST]: { variant: 'corner', rotation: Math.PI },
  [WallDirection.WEST | WallDirection.NORTH]: { variant: 'corner', rotation: Math.PI / 2 },

  // Three connections (tee)
  [WallDirection.NORTH | WallDirection.EAST | WallDirection.SOUTH]: { variant: 'tee', rotation: -Math.PI / 2 },
  [WallDirection.EAST | WallDirection.SOUTH | WallDirection.WEST]: { variant: 'tee', rotation: Math.PI },
  [WallDirection.SOUTH | WallDirection.WEST | WallDirection.NORTH]: { variant: 'tee', rotation: Math.PI / 2 },
  [WallDirection.WEST | WallDirection.NORTH | WallDirection.EAST]: { variant: 'tee', rotation: 0 },

  // Four connections (cross)
  [WallDirection.NORTH | WallDirection.EAST | WallDirection.SOUTH | WallDirection.WEST]: { variant: 'cross', rotation: 0 },
};

class WallConnectionSystemImpl {
  // Map of "tileX,tileZ" -> entity for quick wall lookup
  private wallGrid: Map<string, Entity> = new Map();

  // Callback to get wall component from entity
  private getWallComponent: ((entity: Entity) => WallComponent | undefined) | null = null;

  // Callback to update wall variant in renderer
  private onVariantChange: ((entity: Entity, variant: WallVariant, rotation: number) => void) | null = null;

  /**
   * Set the callback to get wall component from entity
   */
  setGetWallComponent(fn: (entity: Entity) => WallComponent | undefined): void {
    this.getWallComponent = fn;
  }

  /**
   * Set callback for when wall variant changes (for renderer updates)
   */
  setOnVariantChange(fn: (entity: Entity, variant: WallVariant, rotation: number) => void): void {
    this.onVariantChange = fn;
  }

  /**
   * Generate tile key for grid lookup
   */
  private getTileKey(tileX: number, tileZ: number): string {
    return `${tileX},${tileZ}`;
  }

  /**
   * Register a new wall segment
   */
  registerWall(entity: Entity, tileX: number, tileZ: number): void {
    const key = this.getTileKey(tileX, tileZ);
    this.wallGrid.set(key, entity);

    // Update this wall and its neighbors
    this.updateWallAndNeighbors(tileX, tileZ);
  }

  /**
   * Unregister a wall segment (when destroyed)
   */
  unregisterWall(tileX: number, tileZ: number): void {
    const key = this.getTileKey(tileX, tileZ);
    this.wallGrid.delete(key);

    // Update neighbor walls (they need to disconnect from this position)
    const neighbors = this.getNeighborPositions(tileX, tileZ);
    for (const [nx, nz] of neighbors) {
      const neighborEntity = this.getWallAt(nx, nz);
      if (neighborEntity !== null) {
        this.updateWall(nx, nz, neighborEntity);
      }
    }
  }

  /**
   * Get wall entity at tile position
   */
  getWallAt(tileX: number, tileZ: number): Entity | null {
    const key = this.getTileKey(tileX, tileZ);
    return this.wallGrid.get(key) ?? null;
  }

  /**
   * Get neighbor positions (N, E, S, W)
   */
  private getNeighborPositions(tileX: number, tileZ: number): [number, number][] {
    return [
      [tileX, tileZ + 1],  // North (+Z)
      [tileX + 1, tileZ],  // East (+X)
      [tileX, tileZ - 1],  // South (-Z)
      [tileX - 1, tileZ],  // West (-X)
    ];
  }

  /**
   * Calculate connection bitmask based on neighbor walls
   */
  calculateConnections(tileX: number, tileZ: number): number {
    let connections = 0;

    // Check North (+Z)
    if (this.getWallAt(tileX, tileZ + 1) !== null) {
      connections |= WallDirection.NORTH;
    }

    // Check East (+X)
    if (this.getWallAt(tileX + 1, tileZ) !== null) {
      connections |= WallDirection.EAST;
    }

    // Check South (-Z)
    if (this.getWallAt(tileX, tileZ - 1) !== null) {
      connections |= WallDirection.SOUTH;
    }

    // Check West (-X)
    if (this.getWallAt(tileX - 1, tileZ) !== null) {
      connections |= WallDirection.WEST;
    }

    return connections;
  }

  /**
   * Get variant and rotation from connection bitmask
   */
  getVariantFromConnections(connections: number): VariantInfo {
    return CONNECTION_TO_VARIANT[connections] || { variant: 'single', rotation: 0 };
  }

  /**
   * Update a single wall's connections and variant
   */
  private updateWall(tileX: number, tileZ: number, entity: Entity): void {
    if (!this.getWallComponent) return;

    const wall = this.getWallComponent(entity);
    if (!wall) return;

    // Calculate new connections
    const connections = this.calculateConnections(tileX, tileZ);
    const { variant, rotation } = this.getVariantFromConnections(connections);

    // Check if changed
    if (wall.connections !== connections || wall.variant !== variant || wall.rotation !== rotation) {
      wall.connections = connections;
      wall.variant = variant;
      wall.rotation = rotation;

      // Notify renderer
      if (this.onVariantChange) {
        this.onVariantChange(entity, variant, rotation);
      }
    }
  }

  /**
   * Update wall and all its neighbors
   */
  updateWallAndNeighbors(tileX: number, tileZ: number): void {
    // Update the wall itself
    const entity = this.getWallAt(tileX, tileZ);
    if (entity !== null) {
      this.updateWall(tileX, tileZ, entity);
    }

    // Update all neighbors
    const neighbors = this.getNeighborPositions(tileX, tileZ);
    for (const [nx, nz] of neighbors) {
      const neighborEntity = this.getWallAt(nx, nz);
      if (neighborEntity !== null) {
        this.updateWall(nx, nz, neighborEntity);
      }
    }
  }

  /**
   * Clear all walls (for game reset)
   */
  clear(): void {
    this.wallGrid.clear();
  }
}

// Export singleton instance
export const wallConnectionSystem = new WallConnectionSystemImpl();
